import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, Subject, Subscription, filter, map, of, switchMap, take, tap } from 'rxjs';

import { LoginModel } from '../models/login-info.model';
import { WebsocketAwareNeptuneService } from './websocket-aware-neptune.service';

import { LoginApi, SsoState } from '../../api/login/login.api';
import { UserStateApiModel } from '../../api/users/models/user-state.api.model';
import { UsersApi } from '../../api/users/users.api';
import { HttpVerbsEnum } from '../../api/shared/enums/http-verbs.enum';

import { ConfigService } from '../../Core/Config/services/config.service';
import { userPermissionsEnum } from '../../Core/Users/enums/user-permissions.enum';
import { MfaVerifyModel } from '../../Core/Users/models/mfa.model';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';


import { UserPreferencesService } from '../../Account/UserPreferences/services/user-preferences.service';
import {
    LoginMfaValidationRequiredResponse,
    LoginResponse,
    LoginSSORedirectResponse,
    LoginSuccessResponse,
    isLoginMfaValidationRequiredResponse,
    isLoginSSORedirectResponse
} from '../../api/login/login-response';
import { SessionManagementService } from './session-management.service';
import { McpOauthService } from './mcp-oauth.service';

export enum LoginType {
    LOGIN,
    MFA,
    SSO
}

export interface LoginUserState {
    userId: number;
    accountId: number;
    impersonatorUserId?: number;
    impersonatorAccountId?: number;
};

export interface LoginSuccessMessage {
    action: 'success' | 'accountMismatch' | 'complete' | 'ghostInRequired';
    userState: LoginUserState;
}

export class LoginResult {
    loginType: LoginType;
    ssoResponse?: LoginSSORedirectResponse;
    mfaResponse?: LoginMfaValidationRequiredResponse;
    successResponse?: LoginSuccessResponse;
    userState?: UserStateApiModel;

    constructor(loginResponse: LoginResponse) {
        if (isLoginSSORedirectResponse(loginResponse)) {
            return {
                loginType: LoginType.SSO,
                ssoResponse: loginResponse
            };
        }
        if (isLoginMfaValidationRequiredResponse(loginResponse)) {
            return {
                loginType: LoginType.MFA,
                mfaResponse: loginResponse
            };
        }
        return {
            loginType: LoginType.LOGIN,
            successResponse: loginResponse
            // UserState to be set by caller
        };
    }
}

@Injectable()
export class LoginService {
    static readonly LOGIN_CHANNEL_ID = 'LoginChannel';
    static readonly LOGIN_KEY_ID = 'LoginKey';
    static readonly SESSION_KEY_ID = 'SessionKey';

    private subscriptions: Subscription[] = [];
    private switchAccount$ = new Subject<{
        channel: BroadcastChannel;
        accountId: number;
    }>();
    private ghostIn$ = new Subject<{
        channel: BroadcastChannel;
        userState: LoginUserState;
    }>();
    private loginSuccessfulSubject$ = new Subject<{
        userState: UserStateApiModel;
    }>();
    loginSuccessful$ = this.loginSuccessfulSubject$.asObservable();

    brandGUIDCookie: string = '';

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private loginApi: LoginApi,
        private router: Router,
        private usersApi: UsersApi,
        private websocketAwareNeptuneService: WebsocketAwareNeptuneService,
        private configService: ConfigService,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService,
        private sessionManagementService: SessionManagementService,
        private mcpOauthService: McpOauthService
    ) {
        this.subscriptions.push(
            this.ghostIn$.pipe(
                switchMap(({ channel, userState: ghostInUserState }) => this.loginApi.ghostIn({ userId: ghostInUserState.userId }).pipe(
                    switchMap(() => {
                        this.currentUserStateService.clearState();
                        return this.currentUserStateService.loadUserState$();
                    }),
                    switchMap(() => this.currentUserStateService.getState().pipe(
                        filter(x => !!x),
                        take(1)
                    )),
                    tap((currentUserState) => {
                        if (ghostInUserState.accountId === currentUserState.account.id) {
                            // Ghosted in user did not switch to a different account

                            this.currentUserStateService.ignoreWebsockets = true;
                            this.websocketAwareNeptuneService.sendWebsocketEventInstant('/login/ghostin', HttpVerbsEnum.POST);
                            this.websocketAwareNeptuneService.disconnectFromWebsocket();
                            this.websocketAwareNeptuneService.connectToWebsocket();

                            this.postCompleteMessageAndReload(channel);
                        } else {
                            // Ghosted in user did switch to a different account - switch back to match

                            this.switchAccount$.next({
                                channel: channel,
                                accountId: ghostInUserState.accountId
                            });
                        }
                    })
                ))
            ).subscribe(),
            this.switchAccount$.pipe(
                switchMap(({ channel, accountId }) => this.currentUserStateService.switchAccount(accountId).pipe(
                    tap(() => {
                        this.postCompleteMessageAndReload(channel);
                    })
                ))
            ).subscribe(),
            this.loginSuccessful$.subscribe(({ userState }) => {
                // We have a back-and-forth conversation between this service and the session expired dialog,
                // via the BroadcastChannel. If the accounts don't match, the dialog sends this service the
                // expected accountId, and the login service will switch accounts and notify the
                // dialog after the action.

                const channel = new BroadcastChannel(LoginService.LOGIN_CHANNEL_ID);
                const listener = e => {
                    if (e.origin !== window.location.origin || e.data.key !== LoginService.SESSION_KEY_ID) {
                        return;
                    }

                    const result: LoginSuccessMessage = e.data.result;
                    if (result.action === 'ghostInRequired') {
                        this.ghostIn$.next({
                            channel: channel,
                            userState: result.userState
                        });
                    } else if (result.action === 'accountMismatch' && result.userState.accountId) {
                        this.switchAccount$.next({
                            channel: channel,
                            accountId: result.userState.accountId
                        });
                    } else if (result.action === 'complete') {
                        channel.removeEventListener('message', listener);
                    }
                };
                channel.addEventListener ('message', listener);
                channel.postMessage({
                    key: LoginService.LOGIN_KEY_ID,
                    result: {
                        action: 'success',
                        userState: {
                            userId: userState.currentUserAccount.user?.id,
                            accountId: userState.currentUserAccount.account?.id
                        },
                    } as LoginSuccessMessage,
                });
                this.sessionManagementService.handleSessionRefreshAfterExpiry();

            })
        );
    }

    performSsoLogin(loginModel: LoginModel): Observable<LoginResult> {
        return this.loginApi.performSsoLogin(loginModel.loginInfo.username).pipe(
            switchMap((response) => this.handleLoginResponse(response, loginModel))
        );
    }

    performSsoCodeExchange(code: string, state: SsoState): Observable<LoginResult> {
        return this.loginApi.performSsoCodeExchange(code, state).pipe(
            switchMap((response) => this.handleLoginResponse(response, null))
        );
    }

    performMfaLogin(loginModel: LoginModel, mfaCredentials?: MfaVerifyModel): Observable<LoginResult> {
        return this.loginApi.performMfaLogin(
            loginModel.loginInfo.username,
            loginModel.loginInfo.password,
            loginModel.forceLogin,
            mfaCredentials
        ).pipe(
            switchMap((response) => this.handleLoginResponse(response, loginModel))
        );
    }

    performLogin(loginModel: LoginModel): Observable<LoginResult> {
        this.brandGUIDCookie = this.configService.getBrandGUIDCookie();
        return this.loginApi.performLogin(
            loginModel.loginInfo.username,
            loginModel.loginInfo.password,
            loginModel.forceLogin
        ).pipe(
            switchMap((response) => this.handleLoginResponse(response, loginModel))
        );
    }

    private postCompleteMessageAndReload(channel: BroadcastChannel) {
        channel.postMessage({
            key: LoginService.LOGIN_KEY_ID,
            result: {
                action: 'complete'
            } as LoginSuccessMessage,
        });

        this.router.navigate(['Dashboard']);
        // HACK: refresh the page after redirect, we do this because things don't handle refreshing data properly on loadUserState,
        // and localizations don't update either
        requestAnimationFrame(() => {
            window.location.reload();
        });
    }

    private handleLoginResponse(
        loginResponse: LoginResponse,
        loginModel: LoginModel
    ): Observable<LoginResult> {
        const result = new LoginResult(loginResponse);
        if (result.loginType === LoginType.SSO || result.loginType === LoginType.MFA) {
            return of(result);
        }
        return this.postLogin(loginModel).pipe(
            map(userState => {
                result.userState = userState;
                return result;
            })
        );
    }

    // TODO: how to handle different account from login?
    // Switch account BEFORE closing the login popup
    private postLogin(loginModel?: LoginModel): Observable<UserStateApiModel> {
        // If this login is part of an MCP / Connector OAuth flow, hand off to Kong's
        // `/oauth/complete` endpoint instead of routing into the app. The session
        // cookie is now set, so completeFlow() performs a full-page navigation that the
        // browser follows back to Claude's redirect_uri.
        if (this.mcpOauthService.completeFlow()) {
            return of(null);
        }

        this.userPreferencesService.forceGetUserPrefs();
        this.userPreferencesService.refreshDistributionDefaultFooter();

        if (loginModel?.returnUrl) {
            window.location.href = loginModel?.returnUrl;
        } else {
            this.router.navigate(['Dashboard']);
            //  If not on a white-label subdomain and the brand GUID cookie has changed, reload the entire app
            if (!this.configService.isWhiteLabelDomain() && this.brandGUIDCookie !== this.configService.getBrandGUIDCookie()) {
                requestAnimationFrame(() => {
                    window.location.reload();
                });
            }
        }


        return this.usersApi.getCurrentUserState().pipe(
            filter(userStateResponse => !!userStateResponse),
            tap((userStateResponse) => {
                const urlParams = new URLSearchParams(window.location.search);
                if (urlParams.get('noRedirect') === '') {
                    // Remove the URL parameter for the login
                    window.history.pushState({}, document.title, window.location.pathname);
                }
                this.configService.setBrandGUID();
                const userAccount = (userStateResponse as UserStateApiModel)?.currentUserAccount;
                if (userAccount && userAccount.account?.permissionIds.includes(userPermissionsEnum.journodex)) {
                    this.currentUserStateService.navigateToJournodex();
                } else {
                    this.websocketAwareNeptuneService.connectToWebsocket(true);
                }

                this.loginSuccessfulSubject$.next({
                    userState: userStateResponse
                });
            })
        );
    }
}
